import React, { useEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import Popper from "@material-ui/core/Popper";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import PersonIcon from "@material-ui/icons/Person";
import Slide from "@material-ui/core/Slide/Slide";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import userManager from "../../../../../Utils/Oidc/userManager";
import { claimToString } from "../../../../../Utils/Helpers/common";
import A_Paper from "../../../Atoms/Surfaces/Papers/A_Paper";
import A_Button from "../../../../../components/Factory/Atoms/Inputs/Buttons/A_Button";
import { getCRMContactInfo } from "../../../../../Utils/Helpers/common";
import { httpPost } from "../../../../../Utils/apis/apis";
import { resetState } from "../../../../../store/actions/resetStateActions";
import { getSubDomain } from "../../../Pages/Common/User Management/HelperFunctions";
import {
  initTheme,
  initMode,
  getStoredTheme,
  applyTheme,
  toggleMode,
  THEME_OPTIONS,
} from "./M_ThemeSelector/M_ThemeSelector.helper";
import { initFont } from "./M_FontSelector/M_FontSelector.helper";
import { initDisplaySettings } from "./M_DisplaySettings/M_DisplaySettings.helper";
import { initPreferenceSync } from "../../../../Utils/Oidc/preferencesService";
import M_ProfileMenu from "./M_ProfileMenu";
import { getAllUsers, getUserImages } from "../../../../Utils/Oidc/apiService";
import { setAllUsers } from "../../../../../store/actions/userActions";
import useStyles from "./M_UserArea.styles";

const EMPTY_ARRAY = [];

const M_UserArea = React.memo(function M_UserArea(props) {
  const classes = useStyles();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [anchorEl, setAnchorEl] = useState(null);
  const { user } = props;
  const [logoImage, setLogoImage] = useState("");
  let { crmContactUserEmail } = getCRMContactInfo();
  const dispatch = useDispatch();
  const userid = useSelector((state) =>
    state?.getLoggedInUserReducer?.data?.[0]?.email || crmContactUserEmail
  );
  const allUsers = useSelector((state) => state.allUsers?.users || EMPTY_ARRAY);
  
  const currentUserFromStore = useMemo(() => 
    allUsers.find((u) => u.email === userid),
    [allUsers, userid]
  );
  
  const profileImageFromStore = currentUserFromStore?.image;
  const handleUserMenu = (event) => {
    setAnchorEl(anchorEl !== null ? null : event.currentTarget);
  };

  const givenName = claimToString(user.profile.given_name);
  const familyName = claimToString(user.profile.family_name);
  let loggedInUser = `${givenName} ${familyName}`;
  const userInitials = `${givenName.charAt(0)}${familyName.charAt(0)}`.toUpperCase();

  const getImage = useCallback((userID) => {
    const subdomain = getSubDomain();
    fetch(`${process.env.DOCUMENT_HELPER}`, {
      method: "GET",
      headers: {
        filename: "profile_" + userID,
        type: "image",
        "file-operation": "download",
        "process-file": "yes",
        "x-functions-key": process.env.DOCUMENT_HELPER_KEY,
        subdomain: subdomain,
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + user.access_token,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        return response;
      })
      .then((success) => success.blob())
      .then((data) => {
        const imageAsUrl = URL.createObjectURL(data);
        setLogoImage(imageAsUrl);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("failed to get Image:", error);
      });
  }, [user.access_token]);
  const syncUserImages = useCallback(async () => {
    try {
      const usersRes = await getAllUsers();
      if (usersRes && usersRes.Contacts) {
        const users = usersRes.Contacts;
        const emails = users
          .map((u) => u.Email || u.email || u.EmailAddress)
          .filter((e) => e);

        if (emails.length > 0) {
          const profileImageKeys = emails.map((email) => `profile_${email}`);
          const imagesRes = await getUserImages(profileImageKeys);
          const images = imagesRes && imagesRes.image ? imagesRes.image : [];

          const combinedUsers = users.map((user) => {
            const email = user.Email || user.email || user.EmailAddress;
            const userImage = images.find(
              (img) => img.file === `profile_${email}`
            );
            return {
              ...user,
              email: email,
              image: userImage ? userImage.link : null,
            };
          });
          
          dispatch(setAllUsers(combinedUsers));
        } else {
          dispatch(setAllUsers(users));
        }
        // Update last sync time on success
        localStorage.setItem("lastUserImageSync", Date.now().toString());
      }
    } catch (error) {
      console.error("Background sync of user images failed:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    const LAST_SYNC_KEY = "lastUserImageSync";
    const SYNC_INTERVAL_MS = 180000; // 3 minutes

    let timeoutId;

    const runSyncCycle = () => {
      const lastSync = localStorage.getItem(LAST_SYNC_KEY);
      const now = Date.now();
      const elapsed = lastSync ? now - parseInt(lastSync, 10) : SYNC_INTERVAL_MS;

      if (elapsed >= SYNC_INTERVAL_MS) {
        syncUserImages();
        return SYNC_INTERVAL_MS;
      }
      return SYNC_INTERVAL_MS - elapsed;
    };

    const scheduleNext = (delay) => {
      timeoutId = setTimeout(() => {
        const nextDelay = runSyncCycle();
        scheduleNext(nextDelay);
      }, delay);
    };

    // Start the cycle
    const initialDelay = runSyncCycle();
    scheduleNext(initialDelay);

    return () => clearTimeout(timeoutId);
  }, [syncUserImages]);

  useEffect(() => {
    if (userid && userid !== "" && userid !== null) {
      getImage(userid);
    }
  }, [userid]);

  useEffect(() => {
    const handleProfileUpdate = (event) => {
      if (event.detail && event.detail.email === userid) {
        getImage(userid);
      }
    };
    window.addEventListener("profileUpdate", handleProfileUpdate);
    return () => window.removeEventListener("profileUpdate", handleProfileUpdate);
  }, [userid]);

  useEffect(() => {
    initTheme();
    initMode();
    initFont();
    initDisplaySettings();
    initPreferenceSync();
  }, []);

  useEffect(() => {
    const handleThemeShortcut = (event) => {
      const hasModifier =
        (event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey;

      if (!hasModifier) {
        return;
      }

      // Ctrl/Cmd + M → toggle light / dark mode
      if (event.key === "m" || event.key === "M") {
        event.preventDefault();
        toggleMode();
        return;
      }

      let direction = 0;
      if (event.key === "ArrowRight") {
        direction = 1;
      } else if (event.key === "ArrowLeft") {
        direction = -1;
      } else {
        return;
      }

      event.preventDefault();
      const themes = THEME_OPTIONS;
      if (!themes || themes.length === 0) {
        return;
      }
      const current = getStoredTheme();
      const currentIndex = themes.findIndex((t) => t.value === current);
      const safeIndex = currentIndex === -1 ? 0 : currentIndex;
      const nextIndex = (safeIndex + direction + themes.length) % themes.length;
      applyTheme(themes[nextIndex].value);
    };

    window.addEventListener("keydown", handleThemeShortcut);
    return () => window.removeEventListener("keydown", handleThemeShortcut);
  }, []);

  const handleClose = (clickedMenu) => {
    setAnchorEl(null);
    switch (clickedMenu) {
      case "logout":
        handleLogout();
        break;
      case "profile_settings":
        handleServerApp_Redirect("profile_settings");
        break;
      case "change_password":
        handleServerApp_Redirect("change_password");
        break;
      default:
        setAnchorEl(null);
    }
  };

  const handleLogout = async () => {
    let data = {
      activityType: "Logout",
      message: "Logged out from system",
    };
    await httpPost(
      `/Common/WriteDBLog?activityType=logout&message=Logged out from system`,
      data
    );
    userManager.removeUser().catch((error) => {
      console.error("Error removing user:", error);
    });
    userManager.signoutRedirect().catch((error) => {
      console.error("Error signing out and redirecting:", error);
    });
    dispatch(resetState());
    localStorage.clear();
    sessionStorage.clear();
  };
  const handleServerApp_Redirect = (redirectTo) => {
    if (redirectTo === "change_password") {
      window.location.href = `${
        process.env.OIDC_AUTHORITY
      }/Account/ForgetPassword?username=${btoa(user.profile.name)}`;
    } else {
      window.location.href = `${
        process.env.OIDC_AUTHORITY
      }/Account/Manage?username=${btoa(user.profile.name)}`;
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popper" : undefined;

  return (
    <React.Fragment>
      <div style={{ display: "flex" }}>
        <A_Button
          label={isMobile ? "" : loggedInUser ? loggedInUser : ""}
          variant="text"
          style={{ textTransform: "none", color: "#fff" }}
          startIcon={
            profileImageFromStore || logoImage ? (
              <img
                src={profileImageFromStore || logoImage}
                alt={loggedInUser}
                className={classes.userAvatar}
              />
            ) : userInitials ? (
              <span className={classes.userInitials}>{userInitials}</span>
            ) : (
              <PersonIcon className={classes.personicon} />
            )
          }
          onClick={(e) => handleUserMenu(e)}
          className={classes.person}
        ></A_Button>
      </div>
      <Popper
        className={classes.paper}
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
      >
        <Slide direction="down" in={open} mountOnEnter unmountOnExit>
          <div>
            <ClickAwayListener onClickAway={(e) => handleUserMenu(e)}>
              <div>
                <A_Paper
                  width={30}
                  height={"auto"}
                  content={
                    <M_ProfileMenu
                      handleClose={(e) => handleClose(e)}
                      {...props}
                      logoImage={profileImageFromStore || logoImage}
                    />
                  }
                  size="large"
                />
              </div>
            </ClickAwayListener>
          </div>
        </Slide>
      </Popper>
    </React.Fragment>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.user === nextProps.user &&
    prevProps.currentPageId === nextProps.currentPageId
  );
});

M_UserArea.propTypes = {
  callback: PropTypes.func,
  layouts: PropTypes.array,
  currentPageId: PropTypes.string,
  user: PropTypes.object,
};

export default withTranslation()(M_UserArea);
